import React, { useEffect, useState } from 'react';
import { CalendarCheck, Check, X } from 'lucide-react';
import { calendarService } from '../services/calendarService';
import { useLanguage } from '../i18n';
import { CARD_ACTION_ROW, CARD_BTN_FIT, CARD_BTN_LABEL, CARD_ICON } from './cards/cardLayout';

// #536 (PO 2026-11-02) — the GUEST side of an invitation to MORE than 5 different
// events: ONE numbered invitation. Its numbers are the guest's own and stay
// stable ("decline 3" always means the same event). Accept all / Decline all
// answer every event still pending; the hint under the buttons says the guest
// may answer only some of them by writing it ("Accept 3 and decline the rest").
//
// `PackageInvitationLines` is the numbered list (also used by the Calendar
// panel's invitation entry). The card itself is rendered in the Main Chat from
// the `event_invitation_package` metadata PAI attaches after an answer by
// number that left events pending.

export const formatInvitationWhen = (e, locale) => {
    if (!e?.start_time) return '';
    const d = new Date(e.start_time);
    if (Number.isNaN(d.getTime())) return '';
    const day = d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    const hm = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return `${day}, ${hm}`;
};

export const PackageInvitationLines = ({ events = [], locale, t, testid = 'guest-package-list' }) => (
    <ol data-testid={testid} className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {events.map((e) => (
            <li
                key={`${e.n}-${e.start_time}`}
                data-testid="guest-package-row"
                data-n={e.n}
                className="flex items-baseline gap-2 min-w-0 rounded border border-pai-hairline px-2 py-1"
            >
                <span className="shrink-0 font-semibold text-pai-secondary tnum">{e.n}.</span>
                <span className="min-w-0 flex-1 break-words text-white">{e.title || t('caleUntitledEvent', 'Untitled event')}</span>
                <span className="shrink-0 text-pai-meta tnum">
                    {formatInvitationWhen(e, locale)}
                    {e.count > 1 ? ` ×${e.count}` : ''}
                </span>
            </li>
        ))}
    </ol>
);

const GuestPackageInvitationCard = ({ invitation }) => {
    const { t, language } = useLanguage();
    const locale = language === 'es' ? 'es-ES' : 'en-GB';
    const card = invitation || {};
    const events = Array.isArray(card.events) ? card.events : [];
    const [state, setState] = useState('pending'); // pending | confirmed | declined | answered
    const [busy, setBusy] = useState(false);

    // A reload of an old reply: if its lead invitation is no longer pending, the
    // list was answered meanwhile (here, on Telegram, in the Bell) — say so.
    useEffect(() => {
        if (!card.invitation_id) return undefined;
        let alive = true;
        calendarService.listPendingInvitations()
            .then((data) => {
                if (!alive) return;
                const rows = data?.invitations || [];
                const open = rows.some((r) => r.invitation_id === card.invitation_id
                    || (r.package?.invitation_ids || []).includes(card.invitation_id));
                if (!open) setState((s) => (s === 'pending' ? 'answered' : s));
            })
            .catch(() => { /* keep the buttons */ });
        return () => { alive = false; };
    }, [card.invitation_id]);

    const respond = async (decision) => {
        if (!card.invitation_id || busy) return;
        setBusy(true);
        try {
            await calendarService.respondToInvitation(card.invitation_id, decision);
            setState(decision);
            try {
                window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated', { detail: { invitation_id: card.invitation_id } }));
            } catch (_) { /* no-op */ }
        } catch (_) {
            setState('answered');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="guest-package-invitation-card"
            data-state={state}
            data-count={events.length}
            className="relative p-3 min-w-0 max-w-full bg-white/[0.03] border border-pai rounded-lg text-xs"
        >
            <div className="flex items-center gap-2 mb-2 min-w-0">
                <CalendarCheck size={14} className="text-amber-400 shrink-0" />
                <span className="font-medium text-white min-w-0 break-words">
                    {t('invitePkgTitle', 'Invitation from {name}', { name: card.inviter_name || t('invitePkgSomeone', 'someone') })}
                </span>
                <span className="ml-auto text-pai-meta shrink-0">
                    {t('invitePkgPendingCount', '{n} of {total} pending', { n: events.length, total: card.total || events.length })}
                </span>
            </div>
            <PackageInvitationLines events={events} locale={locale} t={t} />
            {state === 'pending' ? (
                <>
                    <div className={CARD_ACTION_ROW}>
                        <button
                            type="button"
                            data-testid="guest-package-accept-all"
                            disabled={busy}
                            onClick={() => respond('confirmed')}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-ctl border border-pai-green/40 bg-pai-green/[0.1] text-pai-green hover:bg-pai-green/[0.18] disabled:opacity-50 ${CARD_BTN_FIT}`}
                        >
                            <Check size={12} className={CARD_ICON} />
                            <span className={CARD_BTN_LABEL}>{t('invitePkgAcceptAll', 'Accept all')}</span>
                        </button>
                        <button
                            type="button"
                            data-testid="guest-package-decline-all"
                            disabled={busy}
                            onClick={() => respond('declined')}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-ctl border border-pai text-pai-secondary hover:text-pai-red hover:border-pai-red/40 disabled:opacity-50 ${CARD_BTN_FIT}`}
                        >
                            <X size={12} className={CARD_ICON} />
                            <span className={CARD_BTN_LABEL}>{t('invitePkgDeclineAll', 'Decline all')}</span>
                        </button>
                    </div>
                    <p data-testid="guest-package-hint" className="mt-2 text-[11px] text-pai-meta break-words">
                        {t('invitePkgHint')}
                    </p>
                </>
            ) : (
                <p data-testid="guest-package-resolution" className="mt-2 text-pai-secondary break-words">
                    {state === 'confirmed' && t('invitePkgAccepted', 'Accepted — they are on your calendar.')}
                    {state === 'declined' && t('invitePkgDeclined', 'Declined.')}
                    {state === 'answered' && t('invitePkgAnswered', 'This invitation has already been answered.')}
                </p>
            )}
        </div>
    );
};

export default GuestPackageInvitationCard;
